import { Box, ListItem, ListItemText } from '@mui/material';
import { Message } from '../../../type';
import React from 'react';
import { format } from 'date-fns';
import { MessageUploadStatus } from '../local-file-message/MessageUploadStatus';
import { MediaUploadErrorIndicator } from '../local-file-message/MediaUploadErrorIndicator';
import { useContextMenu } from './useContextMenu';
import { useDeleteMessage } from '../../../hooks/firebase/useDeleteMessage';
import { DeleteMessageMenu } from './DeleteMessageMenu';

type MyBalloonProps = {
  backgroundColor?: string;
  border?: string;
  children: React.ReactNode;
};

export const MyBalloon = (props: MyBalloonProps) => {
  return (
    <Box
      sx={{
        borderRadius: '30px 0px 30px 30px',
        bgcolor: props.backgroundColor ?? '#3b4559',
        border: props.border ?? 'unset',
        color: 'white',
        px: 4,
        py: 2,
      }}
    >
      {props.children}
    </Box>
  );
};

type MyMessageContainerProps = {
  message: Message;
  standalone?: boolean;
  children: () => React.ReactNode;
};

export const MyMessageContainer = React.forwardRef<
  HTMLLIElement,
  MyMessageContainerProps
>((props, ref) => {
  const {
    messageType,
    guid,
    channelGuid,
    scanStatus,
    localMessage,
    mediaDeleted,
  } = props.message;
  const formattedTime = format(props.message.createdDate.toDate(), 'HH:mm a');
  const { contextMenu, handleContextMenu, closeContextMenu } = useContextMenu();
  const deleteAction = useDeleteMessage(channelGuid, guid);
  return (
    <ListItem
      ref={ref}
      onContextMenu={handleContextMenu}
      sx={{
        display: 'flex',
        flexWrap: 'nowrap',
        alignItems: 'flex-end',
        flexDirection: 'column',
        py: 2,
        px: 0,
        cursor: mediaDeleted ? 'default' : 'context-menu',
        contentVisibility: 'auto',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'nowrap',
          flexDirection: 'row-reverse',
          justifyContent: 'flex-start',
          alignItems: 'flex-end',
          width: '100%',
        }}
      >
        <ListItemText
          sx={{ flex: 'unset', maxWidth: props.standalone ? '100%' : '70%' }}
          primary={props.children()}
          primaryTypographyProps={{ component: 'div' }}
        />
        <ListItemText
          sx={{ flexShrink: 0 }}
          secondary={
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <MediaUploadErrorIndicator
                message={props.message}
                sx={{ mb: 2.5 }}
              />
              <Box sx={{ textAlign: 'end', pr: 2.5, color: '#898f9b' }}>
                {formattedTime}
              </Box>
            </Box>
          }
          secondaryTypographyProps={{
            component: 'div',
            width: 'fit-content',
            ml: 'auto',
          }}
        />
      </Box>
      {localMessage && (messageType === 'PDF' || messageType === 'WORD') && (
        <MessageUploadStatus
          channelGuid={channelGuid}
          messageGuid={guid}
          scanStatus={scanStatus}
        />
      )}
      {!mediaDeleted && (
        <DeleteMessageMenu
          contextMenu={contextMenu}
          closeContextMenu={closeContextMenu}
          onDelete={deleteAction}
        />
      )}
    </ListItem>
  );
});

MyMessageContainer.displayName = 'MyMessageContainer';
